import React, { useState, useEffect } from 'react';
import { Hospital } from '../data/mockData';
import { colors } from '../theme';
import api from '../services/api';

const Hospitals: React.FC = () => {
  const [allHospitals, setAllHospitals] = useState<Hospital[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedHospital, setSelectedHospital] = useState<Hospital | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.hospitals.getAll()
      .then(res => {
        const data = res.data || res;
        if (Array.isArray(data)) setAllHospitals(data);
      })
      .catch((e) => setError(e.message || 'Failed to load data'))
      .finally(() => setLoading(false));
  }, []);

  const handleToggleBlacklist = async (h: Hospital) => {
    setActionLoading(h.id);
    try {
      await api.hospitals.toggleBlacklist(h.id);
      setAllHospitals(prev => prev.map(hospital =>
        hospital.id === h.id
          ? { ...hospital, isBlacklisted: !hospital.isBlacklisted, status: !hospital.isBlacklisted ? 'blacklisted' : 'active' }
          : hospital
      ));
    } catch (err: any) {
      alert('Action failed: ' + (err.message || 'Unknown error'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleView = (h: Hospital) => {
    setSelectedHospital(selectedHospital?.id === h.id ? null : h);
  };

  const filtered = allHospitals.filter(h => {
    if (statusFilter !== 'all' && h.status !== statusFilter) return false;
    if (typeFilter === 'nabh' && !h.nabhAccredited) return false;
    if (typeFilter === 'public' && h.hospitalType !== 'public') return false;
    if (typeFilter === 'private' && h.hospitalType !== 'private') return false;
    if (search) {
      const q = search.toLowerCase();
      return h.name.toLowerCase().includes(q) || h.city.toLowerCase().includes(q) || h.district.toLowerCase().includes(q);
    }
    return true;
  });

  const nabhCount = allHospitals.filter(h => h.nabhAccredited).length;
  const publicCount = allHospitals.filter(h => h.hospitalType === 'public').length;
  const privateCount = allHospitals.filter(h => h.hospitalType === 'private').length;

  return (
    <>
      <div className="header">
        <div className="header-left">
          <div>
            <h2>Hospitals</h2>
            <p>{allHospitals.length} hospitals in verification database</p>
          </div>
        </div>
        <div className="header-right">
          <div className="search-bar">
            <span>🔍</span>
            <input placeholder="Search by name, city, district..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="header-btn primary">+ Add Hospital</button>
        </div>
      </div>

      <div className="content">
        {loading && <div style={{ padding: 40, textAlign: 'center', fontSize: 16, color: '#94A3B8' }}>Loading...</div>}
        {error && <div style={{ padding: '12px 20px', background: 'rgba(239,68,68,0.1)', color: '#EF4444', borderRadius: 8, margin: '0 0 16px', fontSize: 13, fontWeight: 600 }}>{error}</div>}
        {/* Stats */}
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
          <div className="stat-card">
            <h3>{allHospitals.length}</h3>
            <p>Total Hospitals</p>
          </div>
          <div className="stat-card">
            <h3>{nabhCount}</h3>
            <p>NABH Accredited</p>
          </div>
          <div className="stat-card">
            <h3>{publicCount}</h3>
            <p>Government</p>
          </div>
          <div className="stat-card">
            <h3>{privateCount}</h3>
            <p>Private</p>
          </div>
          <div className="stat-card" style={{ borderLeft: `3px solid ${colors.error}` }}>
            <h3>{allHospitals.filter(h => h.isBlacklisted).length}</h3>
            <p>Blacklisted</p>
          </div>
        </div>

        <div className="card">
          <div style={{ padding: '12px 20px', borderBottom: '1px solid #E8ECF2', display: 'flex', gap: 16 }}>
            <div className="filter-bar">
              {['all', 'active', 'blacklisted', 'pending'].map(s => (
                <button key={s} className={`filter-chip${statusFilter === s ? ' active' : ''}`}
                  onClick={() => setStatusFilter(s)}>
                  {s === 'all' ? 'All Status' : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
            <div className="filter-bar">
              {['all', 'nabh', 'public', 'private'].map(s => (
                <button key={s} className={`filter-chip${typeFilter === s ? ' active' : ''}`}
                  onClick={() => setTypeFilter(s)}>
                  {s === 'all' ? 'All Types' : s === 'nabh' ? 'NABH' : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="card-body">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Hospital</th>
                    <th>City / District</th>
                    <th>Type</th>
                    <th>NABH</th>
                    <th>Tier</th>
                    <th>Verifications</th>
                    <th>Match Score</th>
                    <th>Status</th>
                    <th>Last Verified</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(h => (
                    <tr key={h.id}>
                      <td style={{ fontWeight: 600 }}>{h.name}</td>
                      <td>
                        <div>{h.city}</div>
                        <div style={{ fontSize: 11, color: colors.textMuted }}>{h.district}</div>
                      </td>
                      <td>
                        <span style={{
                          padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                          background: h.hospitalType === 'public' ? '#EFF6FF' : '#F5F3FF',
                          color: h.hospitalType === 'public' ? '#2563EB' : '#7C3AED'
                        }}>
                          {h.hospitalType === 'public' ? 'Govt' : 'Private'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {h.nabhAccredited ? (
                          <span style={{
                            padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                            background: colors.successSoft, color: colors.success
                          }}>NABH</span>
                        ) : (
                          <span style={{ fontSize: 11, color: colors.textMuted }}>—</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                          background: h.tier <= 2 ? colors.successSoft : colors.warningSoft,
                          color: h.tier <= 2 ? colors.success : colors.warning
                        }}>Tier {h.tier}</span>
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>{h.verificationCount}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{
                            width: 60, height: 6, borderRadius: 3,
                            background: colors.bgSubtle, overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${h.matchScore}%`, height: '100%', borderRadius: 3,
                              background: h.matchScore >= 85 ? colors.success : h.matchScore >= 50 ? colors.warning : colors.error
                            }} />
                          </div>
                          <span style={{
                            fontSize: 12, fontWeight: 700,
                            color: h.matchScore >= 85 ? colors.success : h.matchScore >= 50 ? colors.warning : colors.error
                          }}>{h.matchScore}%</span>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${h.status}`}>
                          <span className="badge-dot" />
                          {h.status.charAt(0).toUpperCase() + h.status.slice(1)}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: colors.textMuted }}>{h.lastVerified}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="action-btn" onClick={() => handleView(h)}>View</button>
                          {!h.isBlacklisted ? (
                            <button className="action-btn reject" disabled={actionLoading === h.id} onClick={() => handleToggleBlacklist(h)}>
                              {actionLoading === h.id ? '...' : 'Blacklist'}
                            </button>
                          ) : (
                            <button className="action-btn approve" disabled={actionLoading === h.id} onClick={() => handleToggleBlacklist(h)}>
                              {actionLoading === h.id ? '...' : 'Restore'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {selectedHospital && (
          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-header">
              <h3>Hospital Detail — {selectedHospital.name}</h3>
              <button className="action-btn" onClick={() => setSelectedHospital(null)}>✕ Close</button>
            </div>
            <div className="card-body padded">
              <div className="grid-2">
                <div>
                  {[
                    ['Name', selectedHospital.name],
                    ['City', selectedHospital.city],
                    ['District', selectedHospital.district],
                    ['Type', selectedHospital.hospitalType === 'public' ? 'Government' : 'Private'],
                    ['Tier', `Tier ${selectedHospital.tier}`],
                  ].map(([label, value]) => (
                    <div className="detail-row" key={label}>
                      <span className="detail-label">{label}</span>
                      <span className="detail-value">{value}</span>
                    </div>
                  ))}
                </div>
                <div>
                  {[
                    ['NABH Accredited', selectedHospital.nabhAccredited ? 'Yes' : 'No'],
                    ['Match Score', `${selectedHospital.matchScore}%`],
                    ['Verifications', String(selectedHospital.verificationCount)],
                    ['Status', selectedHospital.status],
                    ['Last Verified', selectedHospital.lastVerified],
                    ['Blacklisted', selectedHospital.isBlacklisted ? 'Yes' : 'No'],
                  ].map(([label, value]) => (
                    <div className="detail-row" key={label}>
                      <span className="detail-label">{label}</span>
                      <span className="detail-value">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Hospitals;
